import React, { useContext, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { LoginForm as PepLoginForm } from "@pepdirect/ui/login";
import { logEvent } from "@pepdirect/helpers/analyticsLogger";
import { LoginDTO } from "types/user";
import { links } from "config";
import { EVENT_NAMES } from "@pepdirect/shared/types";
import { useLoginMutation } from "services/graphql/generated";

export function LoginForm({
  inModal = false,
}: {
  inModal?: boolean;
}): JSX.Element {
  const [loginMutation, { error, loading }] = useLoginMutation();
  const { open, close } = useContext(ModalContext);
  const { setUser } = useContext(UserContext);
  const router = useRouter();

  useEffect(() => {
    router.prefetch("/category/standalone");
  }, [router]);

  const onSubmit = async (formValue: LoginDTO) => {
    try {
      const { data } = await loginMutation({
        variables: { email: formValue.login, password: formValue.password },
      });
      setUser(data?.login?.id);
      logEvent(data?.login?.id, EVENT_NAMES.userLoginSuccess);
      router.push("/category/standalone");
      close();
    } catch (error) {
      return;
    }
  };

  const goToForgotPassword = () => {
    if (inModal) {
      open("FORGOT_PASSWORD_MODAL");
    } else {
      close();
      router.push("/forgot-password");
    }
  };

  const mappedErrorMessage = useMemo(() => {
    switch (error?.message) {
      case "not_found":
      case "invalid_password":
        return "Oops, that email and password combination isn't correct. Please check and try again or try resetting your password.";
      case "account_locked":
        return "Account locked. Please contact support for assistance.";
      default:
        return "An error has occurred. Please contact us for assistance.";
    }
  }, [error]);

  return (
    <PepLoginForm
      logoSrc={inModal ? links.logoDark : ""}
      onSubmit={onSubmit}
      goToForgotPassword={goToForgotPassword}
      isLoading={loading}
      error={error ? mappedErrorMessage : ""}
    />
  );
}
